<script setup lang="ts">
import { cn } from 'cn'
import { capitalize } from '@docue/utils/string'

import type { AlertProps } from './types'

import { alertIconVariants, alertVariants } from './variants'

const props = defineProps<AlertProps>()
const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()

const visible = ref(true)

const close = () => {
  visible.value = false
  emit('close')
}
</script>

<template>
  <div
    v-if="!closable || visible"
    :class="cn(alertVariants({ color }), props.class)"
    role="alert"
  >
    <slot name="icon">
      <UiIcon
        v-if="icon"
        :name="icon"
        data-alert-icon
        :class="alertIconVariants({ position: 'left' })"
      />
    </slot>
    <slot />
    <div class="flex flex-col gap-2">
      <UiAlertTitle
        v-if="$slots.title || title"
        :class="{ 'pr-7': closable }"
      >
        <slot name="title">
          {{ title }}
        </slot>
      </UiAlertTitle>
      <UiAlertDescription v-if="$slots.description || description">
        <slot name="description">
          {{ description }}
        </slot>
      </UiAlertDescription>
    </div>
    <button
      v-if="closable"
      type="button"
      data-close-button
      :aria-label="capitalize(t('closeAlert'))"
      :class="cn(
        alertIconVariants({ position: 'right' }),
        'opacity-70 rounded transition-opacity hover:opacity-100 outline-none focus-visible:ring-2 ring-neutral-800',
      )"
      @click="close"
    >
      <UiIcon name="docue:x-close" />
    </button>
  </div>
</template>

<i18n lang="json" locale="en-GB">
{
  "closeAlert": "close alert"
}
</i18n>

<i18n lang="json" locale="de">
{
  "closeAlert": "Meldung schließen"
}
</i18n>

<i18n lang="json" locale="fi">
{
  "closeAlert": "sulje ilmoitus"
}
</i18n>

<i18n lang="json" locale="pl">
{
  "closeAlert": "zamknij powiadomienie"
}
</i18n>

<i18n lang="json" locale="sv">
{
  "closeAlert": "stäng meddelande"
}
</i18n>
